// src/scheduling/lib/today-overview.ts
//
// The new-design Today screen (A24, Samer 1 Oct: "I don't want the screen empty at all") opens with
// the NEXT appointment selected and the day at a glance beside it. Everything here is derived from
// the list the page already loads (/api/Appointment); nothing is fetched or invented.
import type { ListAppointmentItem } from '@/types/appointment-api';

const ms = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/** Patient appointments only (blocks are calendar-only), earliest first. Unparseable times last. */
export function dayTimeline(items: readonly ListAppointmentItem[]): ListAppointmentItem[] {
  return items
    .filter((a) => !a.isBlock)
    .slice()
    .sort((a, b) => (ms(a.start) ?? Infinity) - (ms(b.start) ?? Infinity));
}

/**
 * The appointment to select on load: the first UPCOMING one that has not started yet; if every
 * upcoming one has started (running late), the earliest of them; if there are none, the earliest
 * in-progress one; if the day is all done, the most recent completed one (so the screen is never
 * empty while the day has any appointment). Undefined only for an empty day.
 */
export function pickNextAppointment(
  upcoming: readonly ListAppointmentItem[],
  inProgress: readonly ListAppointmentItem[],
  nowMs: number,
  completed: readonly ListAppointmentItem[] = [],
): string | undefined {
  const up = dayTimeline(upcoming);
  const notStarted = up.find((a) => (ms(a.start) ?? -Infinity) >= nowMs);
  const done = dayTimeline(completed);
  return (notStarted ?? up[0] ?? dayTimeline(inProgress)[0] ?? done[done.length - 1])?.id;
}

export interface DayGap {
  start: string;
  end: string;
  minutes: number;
}

/**
 * The next free interval of at least `minMinutes` between now and the end of the last appointment,
 * from the real start/end times. Undefined when the rest of the day is back-to-back or over.
 */
export function nextGap(
  items: readonly ListAppointmentItem[],
  nowMs: number,
  minMinutes = 15,
): DayGap | undefined {
  const timed = dayTimeline(items).filter((a) => ms(a.start) != null && ms(a.end) != null);
  let cursor = nowMs;
  for (const a of timed) {
    const s = ms(a.start)!;
    const e = ms(a.end)!;
    if (e <= cursor) continue;
    if (s - cursor >= minMinutes * 60_000) {
      return {
        start: new Date(cursor).toISOString(),
        end: new Date(s).toISOString(),
        minutes: Math.round((s - cursor) / 60_000),
      };
    }
    cursor = Math.max(cursor, e);
  }
  return undefined;
}
